import { TOURNAMENT_COPY } from '../../shared/tournaments';
import { REVIEW_COPY } from '../../shared/teamReview';
import { EVENT_COPY } from '../../shared/events';
import { SURVEY_STAFF_COPY } from '../../shared/surveys';
import {
  IconBallVolleyball,
  IconCalendarEvent,
  IconChevronRight,
  IconClipboardCheck,
  IconCreditCardRefund,
  IconMail,
  IconSchool,
  IconTrophy,
  IconUser,
  IconWalk,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import type { Issue } from '../../shared/contracts';
import type { PortalSection } from '../../shared/pages';
import { issueSummary, type SectionIssues } from '../issues';
import { IssueDot } from './IssueDot';
import { FORM_COPY } from './formCopy';
import { SectionCard } from './SectionCard';

interface HomeSection {
  value: PortalSection;
  label: string;
  description: string;
  icon: ReactNode;
}

const SECTIONS: readonly HomeSection[] = [
  {
    value: 'profile',
    label: 'Profile',
    description: 'Update your personal info',
    icon: <IconUser size={24} aria-hidden />,
  },
  {
    value: 'league',
    label: 'League',
    description: 'Your teams and their rosters',
    icon: <IconBallVolleyball size={24} aria-hidden />,
  },
  {
    value: 'tournaments',
    label: TOURNAMENT_COPY.title,
    description: TOURNAMENT_COPY.homeDescription,
    icon: <IconTrophy size={24} aria-hidden />,
  },
  {
    value: 'clinics',
    label: 'Clinics',
    description: "Clinics you've signed up for",
    icon: <IconSchool size={24} aria-hidden />,
  },
  {
    value: 'dropin',
    label: 'Drop-in',
    description: 'Book a fun social drop-in session',
    icon: <IconWalk size={24} aria-hidden />,
  },
  {
    value: 'events',
    label: EVENT_COPY.title,
    description: EVENT_COPY.homeDescription,
    icon: <IconCalendarEvent size={24} aria-hidden />,
  },
];

const STAFF_SECTIONS: readonly HomeSection[] = [
  {
    value: 'event-forms',
    label: FORM_COPY.nav.tileLabel,
    description: FORM_COPY.nav.tileDescription,
    icon: <IconClipboardCheck size={24} aria-hidden />,
  },
  {
    value: 'team-review',
    label: REVIEW_COPY.title,
    description: REVIEW_COPY.homeDescription,
    icon: <IconTrophy size={24} aria-hidden />,
  },
  {
    value: 'attendance',
    label: 'Drop-in Attendance',
    description: 'Take the roll at a drop-in',
    icon: <IconClipboardCheck size={24} aria-hidden />,
  },
  {
    value: 'watch-form',
    label: SURVEY_STAFF_COPY.action,
    description: SURVEY_STAFF_COPY.homeDescription,
    icon: <IconClipboardCheck size={24} aria-hidden />,
  },
  {
    value: 'manage-dropins',
    label: 'Manage drop-ins',
    description: 'Refund and remove participants',
    icon: <IconCreditCardRefund size={24} aria-hidden />,
  },
  {
    value: 'impersonate',
    label: 'Impersonate',
    description: 'See the portal as a member does',
    icon: (
      <span aria-hidden className="text-2xl leading-none">
        👀
      </span>
    ),
  },
  {
    value: 'change-email',
    label: 'Change email',
    description: 'Give a member a new sign-in address',
    icon: <IconMail size={24} aria-hidden />,
  },
];

const NO_ISSUES: SectionIssues = {};

export function HomeTab({
  onOpen,
  issues = NO_ISSUES,
  sections,
}: {
  onOpen: (section: PortalSection) => void;
  issues?: SectionIssues;
  sections: ReadonlySet<PortalSection>;
}) {
  const visible = (section: HomeSection) => sections.has(section.value);
  const staff = STAFF_SECTIONS.filter(visible);
  return (
    <nav aria-label="Portal sections" className="flex flex-col gap-6">
      <SectionCard title="Your membership">
        <SectionList
          sections={SECTIONS.filter(visible)}
          issues={issues}
          onOpen={onOpen}
        />
      </SectionCard>
      {staff.length > 0 ? (
        <SectionCard title="Staff">
          <SectionList sections={staff} issues={issues} onOpen={onOpen} />
        </SectionCard>
      ) : null}
    </nav>
  );
}

const NONE: readonly Issue[] = [];

function SectionList({
  sections,
  issues,
  onOpen,
}: {
  sections: readonly HomeSection[];
  issues: SectionIssues;
  onOpen: (section: PortalSection) => void;
}) {
  return (
    <ul className="flex list-none flex-col gap-4 p-0">
      {sections.map((section) => (
        <li key={section.value}>
          <HomeSectionButton
            section={section}
            issues={issues[section.value] ?? NONE}
            onClick={() => onOpen(section.value)}
          />
        </li>
      ))}
    </ul>
  );
}

function HomeSectionButton({
  section: { value, label, description, icon },
  issues,
  onClick,
}: {
  section: HomeSection;
  issues: readonly Issue[];
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-tour={value === 'league' ? 'league' : undefined}
      className="group border-hairline bg-surface-soft hover:border-brand hover:bg-cream focus-visible:outline-brand-dark portal-press flex w-full cursor-pointer items-center gap-4 rounded-lg border p-4 text-left [--press-scale:0.985] hover:shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 sm:gap-5 sm:p-5"
    >
      <span className="border-hairline bg-surface text-brand-dark relative flex h-12 w-12 shrink-0 items-center justify-center self-start rounded-md border">
        {icon}
        <IssueDot issues={issues} />
      </span>
      <span className="flex grow flex-col gap-1">
        <span className="text-ink text-lg font-medium">{label}</span>
        {issues.length > 0 ? (
          <span className="text-attention-text text-sm font-medium">
            {issueSummary(issues)}
          </span>
        ) : (
          <span className="text-body text-sm">{description}</span>
        )}
      </span>
      <IconChevronRight size={20} aria-hidden className="text-ink shrink-0" />
    </button>
  );
}
